// Minimal toast utility: module-level store + a single <ToastHost /> rendered by App.
import { useEffect, useState } from "react";

export interface ToastItem {
    id: number;
    kind: "success" | "error" | "info";
    message: string;
    link?: { url: string; label: string };
}

const TOAST_TTL_MS = 8_000;

let nextId = 1;
let items: ToastItem[] = [];
const listeners = new Set<(items: ToastItem[]) => void>();

function emit(): void {
    for (const listener of listeners) listener(items);
}

function dismiss(id: number): void {
    items = items.filter((t) => t.id !== id);
    emit();
}

export function showToast(toast: Omit<ToastItem, "id">): void {
    const item: ToastItem = { ...toast, id: nextId++ };
    items = [...items, item];
    emit();
    setTimeout(() => dismiss(item.id), TOAST_TTL_MS);
}

export function ToastHost() {
    const [toasts, setToasts] = useState<ToastItem[]>(items);
    useEffect(() => {
        const listener = (next: ToastItem[]) => setToasts(next);
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    }, []);
    if (toasts.length === 0) return null;
    return (
        <div className="toast-host">
            {toasts.map((t) => (
                <div key={t.id} className={`toast ${t.kind}`}>
                    <span>{t.message}</span>
                    {t.link ? (
                        <a href={t.link.url} target="_blank" rel="noopener noreferrer">
                            {t.link.label}
                        </a>
                    ) : null}
                    <button className="icon-btn" onClick={() => dismiss(t.id)} aria-label="Dismiss">
                        ×
                    </button>
                </div>
            ))}
        </div>
    );
}
